import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'elevated' | 'glass' | 'hero' | 'hero-bull' | 'hero-bear' | 'hero-neutral';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  interactive?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', interactive, className, ...rest },
  ref,
) {
  const isRingGradient =
    variant === 'hero' ||
    variant === 'hero-bull' ||
    variant === 'hero-bear' ||
    variant === 'hero-neutral';

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl',
        // Default border for non-gradient-ring variants
        !isRingGradient && 'border border-border',
        variant === 'default' && 'bg-bg-card card-highlight',
        variant === 'elevated' && 'bg-bg-elevated shadow-card card-highlight',
        variant === 'glass' && 'glass',
        // Hero variants — relative for blur orbs, overflow-hidden for mesh,
        // mesh background, gradient ring border, soft ambient glow.
        variant === 'hero' && 'relative overflow-hidden mesh-hero ring-gradient glow-accent',
        variant === 'hero-bull' &&
          'relative overflow-hidden mesh-bull ring-gradient ring-gradient-bull glow-bull',
        variant === 'hero-bear' &&
          'relative overflow-hidden mesh-bear ring-gradient ring-gradient-bear glow-bear',
        variant === 'hero-neutral' &&
          'relative overflow-hidden mesh-neutral ring-gradient ring-gradient-neutral card-highlight',
        interactive &&
          'transition-all duration-200 ease-out-expo hover:border-border-strong hover:translate-y-[-1px] hover:shadow-elevation',
        className,
      )}
      {...rest}
    />
  );
});

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pt-4 pb-3 flex items-center justify-between gap-2', className)} {...rest} />;
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-sm font-semibold text-fg', className)} {...rest} />;
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pb-5', className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-3 border-t border-border/60 flex items-center justify-between', className)} {...rest} />;
}
